<script setup>
import {computed, ref, watch, onMounted, nextTick} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useUserStore} from '@/stores/user'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import CodemirrorShowModalBtn from '@/common/codemirror/CodemirrorShowModalBtn'

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
})

const componentsStore = useComponentsStore()
const userStore = useUserStore()
const itemDataStore = useItemDataStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)

const codeMirror = ref(null)
const mode = ref(undefined)
const value = ref(undefined)

const originalData = computed(() => formStore.value?.originalData)

const tableName = computed(() => formStore.value?.tableName)

const id = computed(() => formStore.value?.id)

const componentId = computed(() => componentsStore.componentIds[tableName.value])

const component = computed(() => componentsStore.items.find(o => o.id === componentId.value))

const nameField = computed(() => componentsStore.nameField(componentId.value))

const item = ref(null)

watch(
    [tableName, id],
    async ([newTableName, newId]) => {
        if (!newTableName || !newId) {
            item.value = null
            return
        }
        item.value = await itemDataStore.get({
            tableName: newTableName,
            id: newId
        })
    },
    {immediate: true}
)

const modalTitle = computed(() => {
    if (item.value) {
        return component.value.title +
            '<span style="padding: 0 10px; color: #999">></span>' +
            item.value[nameField.value] +
            '<span style="padding: 0 10px; color: #999">></span>' +
            props.field.title
    }
    return null
})

const theme = computed(() => userStore.pref('codeMirror_theme'))

const lineNumbers = computed(() => userStore.pref('codeMirror_lineNumbers'))

const lineWrapping = computed(() => userStore.pref('codeMirror_lineWrapping'))

const setMode = async () => {
    if (tableName.value === 'themes_css') {
        const obj = await itemDataStore.get({
            tableName: 'themes_css',
            id: id.value
        })
        mode.value = obj.mode !== '' ? obj.mode : props.field.codeEditorMode
    } else {
        mode.value = props.field.codeEditorMode
    }
}

if (props.field.codeEditorButtonText) {
    setMode()
}

onMounted(() => {
    if (!props.field.codeEditorButtonText) {
        const indent = 2

        value.value = originalData.value?.[props.field.name]
        if (typeof value.value === 'object') {
            value.value = JSON.stringify(value.value, undefined, indent)
        }

        const options = {
            mode: 'application/json',
            lineNumbers: lineNumbers.value !== undefined ? lineNumbers.value : true,
            lineWrapping: lineWrapping.value !== undefined ? lineWrapping.value : true,
            readOnly: true,
            indentUnit: indent,
            tabMode: 'spaces',
            enterMode: 'indent',
            electricChars: false
        }

        if (theme.value) {
            options['theme'] = theme.value
        }

        nextTick(() => {
            if (typeof CodeMirror !== 'undefined' && codeMirror.value) {
                CodeMirror.fromTextArea(codeMirror.value, options)
            }
        })
    }
})
</script>

<template>
    <CodemirrorShowModalBtn v-if="field.codeEditorButtonText" :current-data="currentData" :field="field"
                            :form-id="formId" :modal-title="modalTitle" :mode="mode"/>

    <!-- Without this div CodeMirror replaced the component's parent element. For example,
         FormControlEmailMessageHtmlBody, which embeds it in tabs, resulted in it replacing the tab pane's div, which
         broke the tabs. -->
    <div v-else-if="!field.codeEditorButtonText">
        <textarea ref="codeMirror" :value="value" style="display: none"></textarea>
    </div>
</template>